import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { IndianRupee, CheckCircle2, CalendarClock, Sparkles, ShieldCheck } from "lucide-react";

const Stat = ({ label, value, icon: Icon, color = "#FF3B30" }) => (
  <div className="rounded-2xl border border-foreground/10 bg-white p-5">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="mt-1 font-heading text-3xl font-extrabold tracking-tight">{value}</div>
      </div>
      <div className="grid h-10 w-10 place-items-center rounded-xl text-white" style={{ background: color }}>
        <Icon size={16} />
      </div>
    </div>
  </div>
);

export default function ProviderDashboard() {
  const { provider, user } = useAuth();
  const [stats, setStats] = useState({ total_bookings: 0, confirmed: 0, completed: 0, earnings: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/provider/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/bookings").then(({ data }) => setRecent(data.slice(0, 5))).catch(() => {});
  }, []);

  const kycStatus = provider?.kyc?.status || "pending";
  const notApproved = kycStatus !== "approved";

  return (
    <div className="space-y-6" data-testid="provider-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
            Hey, {user?.name?.split(" ")[0] || "Pro"} 👋
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Here's how your business is doing.</p>
          {user?.code && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-foreground/10 bg-white px-3 py-1 text-xs" data-testid="provider-id-chip">
              <span className="font-semibold text-muted-foreground uppercase tracking-widest">Provider ID</span>
              <span className="font-mono font-bold tracking-widest">{user.code}</span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Link to="/provider/listings" className="btn-outline" data-testid="cta-manage-listings">Manage listings</Link>
          <Link to="/provider/bookings" className="btn-primary" data-testid="cta-view-bookings">View bookings</Link>
        </div>
      </div>

      {notApproved && (
        <div className="rounded-2xl border border-brand-orange/30 bg-brand-orange/5 p-5" data-testid="kyc-banner">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-0.5 shrink-0 text-brand-orange" size={20} />
            <div>
              <div className="font-heading text-base font-bold">Complete your KYC to go live</div>
              <p className="mt-1 text-sm text-muted-foreground">
                Upload Aadhaar / Driving Licence / Voter ID. Your listings become visible once admin approves.
                Current status: <b className="uppercase">{kycStatus}</b>
              </p>
              <Link to="/provider/kyc" className="mt-3 inline-flex text-sm font-semibold text-brand-orange">Complete KYC →</Link>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Earnings" value={`₹${stats.earnings || 0}`} icon={IndianRupee} color="#FF3B30" />
        <Stat label="Total bookings" value={stats.total_bookings} icon={CalendarClock} color="#0A0A0A" />
        <Stat label="Confirmed" value={stats.confirmed} icon={Sparkles} color="#00E5FF" />
        <Stat label="Completed" value={stats.completed} icon={CheckCircle2} color="#16a34a" />
      </div>

      <div>
        <h2 className="font-heading text-xl font-bold tracking-tight">Recent activity</h2>
        {recent.length === 0 ? (
          <div className="mt-3 rounded-2xl border border-dashed p-8 text-center text-sm text-muted-foreground">
            No bookings yet. {notApproved ? "Get KYC approved first." : "Add listings to attract customers."}
          </div>
        ) : (
          <div className="mt-3 space-y-2">
            {recent.map((b) => (
              <div key={b.id} className="flex items-center justify-between rounded-2xl border p-3">
                <div className="flex items-center gap-3">
                  <img src={b.service?.image_url} className="h-10 w-10 rounded-lg object-cover" alt="" />
                  <div>
                    <div className="text-sm font-semibold">{b.service?.title}</div>
                    <div className="text-xs text-muted-foreground">{new Date(b.scheduled_at).toLocaleString("en-IN")}</div>
                  </div>
                </div>
                <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{b.status.replace("_", " ")}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
